// Assembly Board Excel export: a live WIP snapshot of the board as it stands (after its model/search
// filter). It reads the Board's own placement (features/fleetPlacement), so every boat is in the stage the
// stage load shows it in: the journey step it sits at, "Idle" (GRP sent it over, assembly not started) or
// "Not started". Delivered and departed boats are off the floor and not exported.
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MES_STAGES,
  assemblyPctNum,
  mesHolds,
  mesOverdue,
  mesRemaining,
  parseOvDate,
  type OverviewVessel,
} from '@technohull/shared';
import { startOfTodayUtc, type FleetPlacement } from './fleetPlacement.js';
import { currentDatePrefs, excelDateFormat, toExcelDate, type DatePrefs } from '../lib/dates.js';

const DAY = 86_400_000;

export const MES_BOARD_HEAD = ['SAP', 'VESSEL', 'MODEL', 'STAGE', 'DAYS IN STAGE', 'TARGET (d)', 'ASSEMBLY %', 'STEPS LEFT', 'HOLDS', 'EST. DELIVERY', 'OVERDUE'];

/** what the export reads from a placement: its stages, the idle boats and everyone on the floor. */
export type MesBoardPlacement = Pick<FleetPlacement, 'stages' | 'idle' | 'onFloor'>;
export type MesBoardRow = Array<string | number | Date>;

/**
 * The export's rows, in the Board's order: each journey stage GRP → Delivery (longest in the stage
 * first), the idle boats straight after Assembly (where the stage load shows them), then the boats on the
 * floor that have not started.
 *   STAGE          the journey step's label, "Idle" or "Not started"
 *   DAYS IN STAGE  the Board's own figure: days since the boat's production-stage date (for a Delivery
 *                  boat that is its last stage, normally PDI), or the idle days. Never below 0; blank
 *                  when there is no date.
 *   TARGET (d)     the stage's target dwell; blank for Delivery, Idle and Not started (the Board sets none)
 *   OVERDUE        mesOverdue against `today` (UTC midnight of the calendar day), so a boat due today is not
 */
export function mesBoardRows(placement: MesBoardPlacement, today: Date = startOfTodayUtc()): MesBoardRow[] {
  const row = (v: OverviewVessel, stage: string, days: number | '', target: number | ''): MesBoardRow => {
    const del = parseOvDate(v.deliveryDate);
    const pct = assemblyPctNum(v);
    return [
      v.sap, v.name || '', v.model || '', stage, days, target,
      pct ? Math.round(pct) : '', mesRemaining(v).length, mesHolds(v).join(', '),
      del ? toExcelDate(del) : '', mesOverdue(v, today) ? 'YES' : '',
    ];
  };
  const out: MesBoardRow[] = [];
  const seen = new Set<OverviewVessel>();
  const idle = () => {
    for (const i of placement.idle) { seen.add(i.v); out.push(row(i.v, 'Idle', Math.max(0, i.days), '')); }
  };
  let idleDone = false;
  for (const s of placement.stages) {
    const target = MES_STAGES.find((m) => m.key === s.key)?.target ?? '';
    const boats = s.boats
      .map((b) => {
        const enter = b.stage?.enter ? parseOvDate(b.v[b.stage.enter] as string) : null;
        return { v: b.v, days: enter ? Math.max(0, Math.round((+today - +enter) / DAY)) : ('' as const) };
      })
      .sort((a, b) => (b.days || 0) - (a.days || 0));
    for (const b of boats) { seen.add(b.v); out.push(row(b.v, s.label, b.days, target)); }
    if (s.key === 'assembly') { idle(); idleDone = true; }
  }
  if (!idleDone) idle();
  for (const v of placement.onFloor) if (!seen.has(v)) out.push(row(v, 'Not started', '', ''));
  return out;
}

/** write the snapshot as `<title>.xlsx`. Pass the Board's placement and the `today` it was placed against.
 *  EST. DELIVERY is a real Excel date, shown in the user's date format. */
export async function exportMesBoard(
  placement: MesBoardPlacement, today: Date = startOfTodayUtc(), title = 'production-board', prefs: DatePrefs = currentDatePrefs(),
): Promise<string> {
  const XLSX: any = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet([MES_BOARD_HEAD, ...mesBoardRows(placement, today)], { dateNF: excelDateFormat(prefs) });
  ws['!cols'] = [{ wch: 10 }, { wch: 26 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 9 }, { wch: 22 }, { wch: 13 }, { wch: 9 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Production Board');
  const filename = `${title}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}
